import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Users, Shield, BookOpen, TrendingUp, Target, Lightbulb } from 'lucide-react';
export function EducationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-350" data-mp="1w1v56mtqogol">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-351" data-mp="20stqk0tl44yp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-352" data-mp="25rwtnfq08141">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-353" data-mp="2eqs08swynd7v">
            <span className="w-8 h-px bg-teal" data-id="element-354" data-mp="2jqeft909h5ju"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-355" data-mp="ketpunmx0bae">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-356" data-mp="pepy4z2naovr"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-357" data-mp="73feymiymaqj">
            Education and Human Capability Development
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-358" data-mp="3ugrgptqrfhaa">
            SyNeural's platform architecture is designed to help education
            systems build stronger thinkers, more self-directed learners, and
            more capable future citizens.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-359" data-mp="m1g48xpc1obp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-360" data-mp="h122fxebram4">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-361" data-mp="2ws7vqb4cjmmz">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding how students develop judgment, how readiness gaps
            emerge, and how schools and institutions can better connect those
            gaps to meaningful pathways for growth.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-362" data-mp="2u81r017wrs5d">
            The platform is relevant across the full educational lifecycle. At
            early stages, it can support emotional regulation, cooperation,
            self-direction, and foundational reasoning. In primary and secondary
            education, it can help strengthen logic, media literacy, planning,
            resilience against manipulation, and long-range decision-making. In
            higher education, it can support critical thinking, civic
            capability, leadership development, and readiness for professional
            and institutional responsibility.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-363" data-mp="1hym7bff6dabr">
        {/* Early Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-364" data-mp="1d13x7lpf8p26">
          <div className="flex items-center gap-4 mb-6" data-id="element-365" data-mp="20ulsa69qjdlu">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-366" data-mp="1vx3qczu4xync">
              <Users size={28} data-id="element-367" data-mp="2am87edp71wtv" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-368" data-mp="25nyb1zrqgq6o">
              Early Development and Foundational Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-369" data-mp="2jldalxbpaecd">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-370" data-mp="2fnp92bfi5nbi">
              At the earliest stages, SyNeural can support the development of
              core capabilities that shape later learning and long-term
              readiness. These include emotional regulation, impulse control,
              attention stability, cooperation, frustration tolerance, and basic
              cause-and-effect reasoning. By identifying developmental
              bottlenecks early and connecting them to support pathways, the
              platform can help schools and caregivers build stronger
              foundations before problems compound over time.
            </p>
          </div>
        </motion.div>

        {/* Primary and Secondary Education */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-371" data-mp="80co7nazi8xq">
          <div className="flex items-center gap-4 mb-6" data-id="element-372" data-mp="33w8w4zyffykf">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-373" data-mp="2zvoxtt24aowp">
              <BookOpen size={28} data-id="element-374" data-mp="2v4gmusl52vhf" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-375" data-mp="2v2vu98idxmm7">
              Primary and Secondary Education
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-376" data-mp="2l72jnuo88372">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-377" data-mp="2g77t5s8d5fmj">
              In school-age environments, SyNeural can help strengthen the
              capabilities that support both academic performance and broader
              life readiness. This includes reasoning quality, self-direction,
              media literacy, planning ability, emotional stability, and the
              ability to think through consequences. The platform can support
              schools in moving beyond narrow academic outcomes toward a fuller
              model of human capability and judgment development.
            </p>
          </div>
        </motion.div>

        {/* Media Literacy */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-378" data-mp="fjla8ye6d8j7">
          <div className="flex items-center gap-4 mb-6" data-id="element-379" data-mp="1cadum8855mqs">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-380" data-mp="1c7ro8bwzlda5">
              <Shield size={28} data-id="element-381" data-mp="1397alp6a9tss" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-382" data-mp="137w77qgr2o27">
              Media Literacy and Manipulation Resistance
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-383" data-mp="3nz4z7l7tc4sg">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-384" data-mp="3gfvva7i2syas">
              Students are growing up in environments shaped by information
              overload, emotional framing, and highly persuasive media systems.
              SyNeural can help education systems strengthen resilience against
              propaganda, rhetorical manipulation, peer-driven narratives, and
              low-quality information environments. This makes the platform
              especially relevant for media literacy, critical reasoning, and
              the development of more independent and grounded judgment.
            </p>
          </div>
        </motion.div>

        {/* Beyond Grades */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-385" data-mp="31qznmbjxlnap">
          <div className="flex items-center gap-4 mb-6" data-id="element-386" data-mp="1ogrznsm2jr4g">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-387" data-mp="1oibinqz40g9x">
              <Target size={28} data-id="element-388" data-mp="1sk6jkud4orn6" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-389" data-mp="1xgx5hybm6hgi">
              Student Development Beyond Grades
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-390" data-mp="22dx4xqdyklyn">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-391" data-mp="27da0cziwb1cu">
              A major strength of the platform is that it helps make important
              human capabilities more visible beyond conventional grading
              systems. Students may have strong potential but struggle with
              execution, confidence, emotional regulation, or opportunity
              access. Others may perform academically while remaining weak in
              judgment, foresight, or resilience. SyNeural helps create a more
              complete picture of readiness by showing strengths, bottlenecks,
              and development pathways that traditional academic signals often
              miss.
            </p>
          </div>
        </motion.div>

        {/* Higher Education */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-392" data-mp="9jo1n9n17lbl">
          <div className="flex items-center gap-4 mb-6" data-id="element-393" data-mp="xb35x7ydccph">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-394" data-mp="sh8ipujk1fln">
              <GraduationCap size={28} data-id="element-395" data-mp="ninqahajo07r" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-396" data-mp="1kz4cibbzlj29">
              Higher Education and Readiness for Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-397" data-mp="1g61dvoscrjf7">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-398" data-mp="3m5tcyg3nirik">
              In universities and advanced learning environments, SyNeural can
              support critical thinking, long-range planning, leadership
              development, and readiness for complex institutional and
              professional roles. It can help students better understand their
              strengths, identify practical growth areas, and prepare for paths
              that involve greater responsibility in civic life, work,
              entrepreneurship, or public service.
            </p>
          </div>
        </motion.div>

        {/* Support for Educators */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-399" data-mp="doyk7tgkini7">
          <div className="flex items-center gap-4 mb-6" data-id="element-400" data-mp="1797pv5jgd1wc">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-401" data-mp="1b989r5pbm5as">
              <Lightbulb size={28} data-id="element-402" data-mp="xaic9oyla3lw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-403" data-mp="xc32x3hs92eh">
              Support for Educators and Institutions
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-404" data-mp="nj44ktwhhxho">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-405" data-mp="sbnqqf98m0y3">
              SyNeural is designed not only for student insight, but for
              institutional improvement. Schools, universities, and educational
              leaders can use the platform to better understand where capability
              development is strong, where support systems are weak, and where
              educational models may need to evolve. This can support curriculum
              design, student support strategies, developmental planning, and
              broader efforts to connect education with long-term readiness
              rather than short-term performance alone.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-406" data-mp="37w9farepvqt9">
        <div className="max-w-4xl mx-auto" data-id="element-407" data-mp="37u6erdfxufaa">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-408" data-mp="9dc7oje753ha">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-409" data-mp="3qolckzqik1wj">
              Why It Matters for Education
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-410" data-mp="2f7lnse3dk579">
              Education systems do more than deliver content. They shape how
              people think, how they regulate themselves, how they handle
              complexity, and how they prepare for life inside institutions and
              society. SyNeural helps make that broader developmental mission
              more visible and more actionable. It offers a framework for
              helping students not only perform, but grow into more capable,
              resilient, and responsible individuals over time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}